import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  ListsToggle,
  MDXEditor,
  MDXEditorMethods,
  headingsPlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  toolbarPlugin,
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { AlertCircleIcon } from "lucide-react-native";
import { ComponentProps, useEffect, useRef } from "react";
import {
  Controller,
  FieldValues,
  UseFormGetValues,
  UseFormSubscribe,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Box } from "../../ui/box";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputProps } from "../types";

export type FormInputMarkdownProps<TFieldValues extends FieldValues> =
  FormInputProps<TFieldValues, string | undefined | null> & {
    size?: ComponentProps<typeof FormControl>["size"];
    subscribe?: UseFormSubscribe<TFieldValues>;
    getValues?: UseFormGetValues<TFieldValues>;
  };

export default function FormInputMarkdown<TFieldValues extends FieldValues>(
  props: FormInputMarkdownProps<TFieldValues>,
) {
  const {
    control,
    name,
    placeholder,
    label,
    required,
    disabled,
    className,
    size,
    subscribe,
    getValues,
    labelSize = "md",
  } = props;

  const mkdownRef = useRef<MDXEditorMethods>(null);
  const { t } = useTranslation();
  useEffect(() => {
    if (!getValues) return;
    const subscription = subscribe?.({
      name: name,
      formState: { values: true },
      callback: () => {
        const scriptValue = getValues(name);
        if (scriptValue !== mkdownRef.current?.getMarkdown()) {
          mkdownRef.current?.setMarkdown(scriptValue || "");
        }
      },
    });
    return subscription;
  }, [name, subscribe, getValues]);
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { onChange, value, ref, onBlur },
        fieldState: { error },
      }) => {
        return (
          <FormControl
            isInvalid={!!error}
            isRequired={required}
            isDisabled={disabled}
            className={className}
            size={size}
          >
            {!!label && (
              <FormControlLabel>
                <FormControlLabelText size={labelSize}>
                  {label}
                </FormControlLabelText>
              </FormControlLabel>
            )}
            <Box ref={focusScrollRef(ref)}>
              <MDXEditor
                ref={(elm) => {
                  mkdownRef.current = elm;
                }}
                markdown={value || ""}
                onChange={(mkd, initial) => {
                  if (initial) return;
                  onChange(mkd);
                }}
                placeholder={placeholder}
                className="border border-outline-100 rounded-lg my-1"
                contentEditableClassName="markdown-content"
                onBlur={() => {
                  onBlur();
                  if (!mkdownRef.current) return;
                  onChange(mkdownRef.current.getMarkdown());
                }}
                plugins={[
                  headingsPlugin({ allowedHeadingLevels: [4, 5, 6] }),
                  listsPlugin(),
                  linkPlugin(),
                  linkDialogPlugin(),
                  toolbarPlugin({
                    toolbarContents: () => (
                      <>
                        <BoldItalicUnderlineToggles />
                        <BlockTypeSelect />
                        <ListsToggle options={["bullet", "number"]} />
                        <CreateLink />
                      </>
                    ),
                    toolbarClassName:
                      "rounded-b-none font-body bg-background-50 border-b-outline-100 border-b",
                  }),
                ]}
                translation={(
                  key: string,
                  defaultValue: string,
                  interpolations?: Record<string, any>,
                ) => {
                  if (key === "toolbar.blockTypes.heading") {
                    switch (interpolations?.level) {
                      case 1:
                        return t(
                          "toolbar.blockTypes.heading_one",
                          defaultValue,
                          interpolations,
                        ) as string;
                      case 2:
                        return t(
                          "toolbar.blockTypes.heading_two",
                          defaultValue,
                          interpolations,
                        ) as string;
                      case 3:
                        return t(
                          "toolbar.blockTypes.heading_three",
                          defaultValue,
                          interpolations,
                        ) as string;
                      case 4:
                        return t(
                          "toolbar.blockTypes.heading_four",
                          defaultValue,
                          interpolations,
                        ) as string;
                      case 5:
                        return t(
                          "toolbar.blockTypes.heading_five",
                          defaultValue,
                          interpolations,
                        ) as string;
                      case 6:
                        return t(
                          "toolbar.blockTypes.heading_six",
                          defaultValue,
                          interpolations,
                        ) as string;
                    }
                  }
                  return t(key, defaultValue, interpolations ?? {}) as string;
                }}
              />
            </Box>
            <FormControlError>
              <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
              <FormControlErrorText>{error?.message}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
